import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth } from "@/hooks/useAuth";
import { useCryptoPrices } from "@/hooks/useCryptoPrices";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import {
  ArrowUpRight, ArrowDownLeft, RefreshCw, Copy,
  CheckCircle2, Clock, XCircle, Eye, EyeOff, Search,
  TrendingUp, TrendingDown, ChevronLeft, ArrowLeftRight, Loader2,
  Users, Globe
} from "lucide-react";
import { KNOWN_CRYPTO_META } from "@/lib/crypto-data";
import SlideToConfirm from "@/components/SlideToConfirm";
import { QRCodeSVG } from "qrcode.react";
import { useWebAuthn } from "@/hooks/useWebAuthn";
import Web3SendDialog from "@/components/Web3SendDialog";


interface ReceiveAddress { id: string; network: string; address: string; }
interface UserCryptoAddress { id: string; blockchain: string; network: string; address: string; }
interface WalletBalance { id: string; crypto_symbol: string; crypto_name: string; balance: number; }
interface WithdrawalRequest { id: string; crypto_symbol: string; crypto_name: string; amount: number; wallet_address: string; network: string; status: string; tx_hash: string | null; admin_notes: string | null; created_at: string; transaction_number: string | null; }
interface DepositRequest { id: string; crypto_symbol: string; network: string; amount: number; status: string; tx_hash: string | null; admin_notes: string | null; created_at: string; }
interface InternalTransfer { id: string; sender_id: string; recipient_id: string; crypto_symbol: string; amount: number; transaction_number: string | null; sender_name: string | null; recipient_name: string | null; created_at: string; }

const WalletPage = () => {
  const { user, loading: authLoading } = useAuth();
  const { cryptoAssets } = useCryptoPrices();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  
  const [userName, setUserName] = useState<string>("");
  const [userNumericId, setUserNumericId] = useState<string>("");
  const [balances, setBalances] = useState<WalletBalance[]>([]);
  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([]);
  const [deposits, setDeposits] = useState<DepositRequest[]>([]);
  const [internalTransfers, setInternalTransfers] = useState<InternalTransfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendOpen, setSendOpen] = useState(false);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [selectedCrypto, setSelectedCrypto] = useState("");
  const [sendAmount, setSendAmount] = useState("");
  const [recipientId, setRecipientId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hideBalance, setHideBalance] = useState(false);
  const [activeTab, setActiveTab] = useState<"crypto" | "history">("crypto");
  const [receiveAddresses, setReceiveAddresses] = useState<ReceiveAddress[]>([]);
  const [userAddresses, setUserAddresses] = useState<UserCryptoAddress[]>([]);
  const [selectedReceiveNetwork, setSelectedReceiveNetwork] = useState("");
  const [selectedReceiveCrypto, setSelectedReceiveCrypto] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [hideSmallBalances, setHideSmallBalances] = useState(false);
  const [selectedAssetDetail, setSelectedAssetDetail] = useState<string | null>(null);
  const [generatingAddress, setGeneratingAddress] = useState(false);
  // Swap states
  const [swapOpen, setSwapOpen] = useState(false);
  const [swapFrom, setSwapFrom] = useState("");
  const [swapTo, setSwapTo] = useState("");
  const [swapAmount, setSwapAmount] = useState("");
  const [swapSubmitting, setSwapSubmitting] = useState(false);
  // Send mode chooser
  const [sendModeOpen, setSendModeOpen] = useState(false);
  // External send states
  const [externalSendOpen, setExternalSendOpen] = useState(false);
  const [web3SendOpen, setWeb3SendOpen] = useState(false);

  const [sendNetwork, setSendNetwork] = useState("");
  const [sendWalletAddress, setSendWalletAddress] = useState("");
  const [externalSubmitting, setExternalSubmitting] = useState(false);
  const [recipientName, setRecipientName] = useState<string | null>(null);
  const [recipientAvatar, setRecipientAvatar] = useState<string | null>(null);
  const [lookingUpRecipient, setLookingUpRecipient] = useState(false);
  const [expandedHistoryIndex, setExpandedHistoryIndex] = useState<number | null>(null);
  const [biometricVerifying, setBiometricVerifying] = useState(false);
  const { login: loginBiometric, hasCredential: hasBiometric } = useWebAuthn();

  useEffect(() => { if (!authLoading && !user) navigate("/auth"); }, [user, authLoading, navigate]);
  useEffect(() => { if (user) fetchData(); }, [user]);

  // Lookup recipient name when ID is 16 digits
  useEffect(() => {
    if (recipientId.length !== 16) { setRecipientName(null); setRecipientAvatar(null); return; }
    let cancelled = false;
    const lookup = async () => {
      setLookingUpRecipient(true);
      try {
        const { data, error } = await supabase.functions.invoke('internal-transfer', {
          body: { action: 'lookup', recipientId },
        });
        if (!cancelled) {
          if (error || !data?.found) {
            setRecipientName(null);
            setRecipientAvatar(null);
          } else {
            setRecipientName(data.name);
            setRecipientAvatar(data.avatar_url || null);
          }
        }
      } catch {
        if (!cancelled) { setRecipientName(null); setRecipientAvatar(null); }
      } finally {
        if (!cancelled) setLookingUpRecipient(false);
      }
    };
    lookup();
    return () => { cancelled = true; };
  }, [recipientId]);

  const autoGenerateAddresses = async (existingUserAddrs: UserCryptoAddress[]) => {
    const allNetworks = new Set<string>();
    Object.values(KNOWN_CRYPTO_META).forEach(meta => {
      meta.networks.forEach(n => allNetworks.add(n.name));
    });

    const existingNetworks = new Set(existingUserAddrs.map(a => a.network));
    const missingNetworks = [...allNetworks].filter(n => !existingNetworks.has(n));

    if (missingNetworks.length === 0) return;

    const batchSize = 3;
    const newAddresses: UserCryptoAddress[] = [];
    
    for (let i = 0; i < missingNetworks.length; i += batchSize) {
      const batch = missingNetworks.slice(i, i + batchSize);
      const results = await Promise.allSettled(
        batch.map(async (network) => {
          try {
            const { data, error } = await supabase.functions.invoke("generate-address", {
              body: { network },
            });
            if (error || data?.error) {
              console.log(`Skipping ${network}: ${data?.error || error?.message}`);
              return null;
            }
            if (data?.address) {
              return { id: crypto.randomUUID(), blockchain: '', network, address: data.address } as UserCryptoAddress;
            }
            return null;
          } catch (e) {
            console.log(`Failed to generate for ${network}:`, e);
            return null;
          }
        })
      );
      
      results.forEach(r => {
        if (r.status === 'fulfilled' && r.value) {
          newAddresses.push(r.value);
        }
      });
    }

    if (newAddresses.length > 0) {
      setUserAddresses(prev => [...prev, ...newAddresses]);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [walletsRes, withdrawalsRes, depositsRes, addressesRes, userAddrsRes, profileRes, transfersRes] = await Promise.all([
        supabase.from("wallets").select("*").eq("user_id", user!.id).order("balance", { ascending: false }),
        supabase.from("withdrawal_requests").select("*").eq("user_id", user!.id).order("created_at", { ascending: false }),
        supabase.from("deposit_requests").select("*").eq("user_id", user!.id).order("created_at", { ascending: false }),
        supabase.from("receive_addresses").select("*").eq("is_active", true),
        supabase.from("user_crypto_addresses").select("*").eq("user_id", user!.id),
        supabase.from("profiles").select("full_name, email, numeric_id").eq("user_id", user!.id).single(),
        supabase.from("internal_transfers").select("*").or(`sender_id.eq.${user!.id},recipient_id.eq.${user!.id}`).order("created_at", { ascending: false }),
      ]);
      if (profileRes.data) {
        setUserName(profileRes.data.full_name || profileRes.data.email?.split('@')[0] || '');
        setUserNumericId((profileRes.data as any).numeric_id || '');
      }
      if (walletsRes.data) setBalances(walletsRes.data);
      if (withdrawalsRes.data) setWithdrawals(withdrawalsRes.data as WithdrawalRequest[]);
      if (depositsRes.data) setDeposits(depositsRes.data as DepositRequest[]);
      if (transfersRes.data) setInternalTransfers(transfersRes.data as InternalTransfer[]);
      if (addressesRes.data) setReceiveAddresses(addressesRes.data as ReceiveAddress[]);
      const userAddrs = (userAddrsRes.data || []) as UserCryptoAddress[];
      setUserAddresses(userAddrs);
      
      // Auto-generate missing addresses in background
      autoGenerateAddresses(userAddrs);
    } catch (error) { console.error("Error fetching wallet data:", error); }
    finally { setLoading(false); }
  };

  const priceMap = useMemo(() => {
    const map: Record<string, number> = {};
    cryptoAssets.forEach(a => { map[a.symbol] = a.price; });
    return map;
  }, [cryptoAssets]);

  const changeMap = useMemo(() => {
    const map: Record<string, number> = {};
    cryptoAssets.forEach(a => { map[a.symbol] = a.change24h; });
    return map;
  }, [cryptoAssets]);

  const totalUsd = useMemo(() => balances.reduce((sum, b) => sum + b.balance * (priceMap[b.crypto_symbol] || 0), 0), [balances, priceMap]);

  const selectedBalance = balances.find(b => b.crypto_symbol === selectedCrypto);
  const selectedMeta = selectedCrypto ? KNOWN_CRYPTO_META[selectedCrypto] : null;
  const availableNetworks = selectedMeta?.networks || [];

  // Swap calculations
  const swapFromBalance = balances.find(b => b.crypto_symbol === swapFrom);
  const swapFromPrice = priceMap[swapFrom] || 0;
  const swapToPrice = priceMap[swapTo] || 0;
  const swapAmountNum = parseFloat(swapAmount) || 0;
  const swapValueUsd = swapAmountNum * swapFromPrice;
  const swapFeeUsd = swapValueUsd * 0.001; // 0.1%
  const swapReceiveAmount = swapToPrice > 0 ? (swapValueUsd - swapFeeUsd) / swapToPrice : 0;

  const handleSwap = async () => {
    if (!swapFrom || !swapTo || !swapAmount) return;
    if (swapAmountNum <= 0 || !swapFromBalance || swapAmountNum > swapFromBalance.balance) {
      toast({ title: t('common.error'), description: t('common.insufficientBalance') || 'رصيد غير كافٍ', variant: "destructive" });
      return;
    }
    setSwapSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('swap-crypto', {
        body: { fromSymbol: swapFrom, toSymbol: swapTo, fromAmount: swapAmountNum, fromPrice: swapFromPrice, toPrice: swapToPrice },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast({ title: 'تم الاستبدال بنجاح ✅', description: `${swapAmountNum} ${swapFrom} → ${data.toAmount.toFixed(8)} ${swapTo}` });
      setSwapOpen(false); setSwapFrom(""); setSwapTo(""); setSwapAmount("");
      fetchData();
    } catch (err: any) {
      toast({ title: t('common.error'), description: err.message, variant: "destructive" });
    } finally { setSwapSubmitting(false); }
  };

  const handleSend = async () => {
    if (!selectedCrypto || !sendAmount || !recipientId) {
      toast({ title: t('common.error'), description: t('common.fillAllFields') || 'أكمل جميع الحقول', variant: "destructive" }); return;
    }
    const amount = parseFloat(sendAmount);
    if (isNaN(amount) || amount <= 0) {
      toast({ title: t('common.error'), description: t('common.invalidAmount') || 'مبلغ غير صالح', variant: "destructive" }); return;
    }
    if (!selectedBalance || amount > selectedBalance.balance) {
      toast({ title: t('common.error'), description: t('common.insufficientBalance') || 'رصيد غير كافٍ', variant: "destructive" }); return;
    }
    if (recipientId.length !== 16 || !/^\d+$/.test(recipientId)) {
      toast({ title: t('common.error'), description: 'معرّف المستخدم يجب أن يكون 16 رقم', variant: "destructive" }); return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('internal-transfer', {
        body: { recipientId, cryptoSymbol: selectedCrypto, amount, transferMode: 'onchain' },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const txNum = data.transactionNumber ? ` (${data.transactionNumber})` : '';
      const chainInfo = data?.onChain && data?.network ? ` عبر ${data.network}` : '';
      toast({
        title: t('wallet.transferSuccess'),
        description: `${amount} ${selectedCrypto} → ${data.recipientName}${chainInfo}${txNum}`,
      });
      setSendOpen(false); setSendAmount(""); setRecipientId(""); setSelectedCrypto(""); setRecipientAvatar(null);
      fetchData();
    } catch (err: any) {
      toast({ title: t('common.error'), description: err.message, variant: "destructive" });
    } finally { setSubmitting(false); }
  };

  const handleExternalSend = async () => {
    if (!selectedCrypto || !sendAmount || !sendWalletAddress || !sendNetwork) {
      toast({ title: t('common.error'), description: 'أكمل جميع الحقول', variant: "destructive" }); return;
    }
    const amount = parseFloat(sendAmount);
    if (isNaN(amount) || amount <= 0) {
      toast({ title: t('common.error'), description: 'مبلغ غير صالح', variant: "destructive" }); return;
    }
    const bal = balances.find(b => b.crypto_symbol === selectedCrypto);
    if (!bal || bal.balance < amount) {
      toast({ title: t('common.error'), description: t('common.insufficientBalance') || 'رصيد غير كافٍ', variant: "destructive" }); return;
    }
    setExternalSubmitting(true);
    try {
      const res = await fetch(`https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/functions/v1/send-crypto`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({
          toAddress: sendWalletAddress,
          amount,
          cryptoSymbol: selectedCrypto,
          cryptoName: KNOWN_CRYPTO_META[selectedCrypto]?.nameEn || selectedCrypto,
          destinationNetwork: sendNetwork,
        }),
      });
      const data = await res.json();
      if (!res.ok || data?.error) throw new Error(data?.error || `HTTP ${res.status}`);
      const txNum = data.transactionNumber ? ` (${data.transactionNumber})` : '';
      toast({
        title: 'تم الإرسال بنجاح ✅',
        description: `${data.netAmount?.toFixed(8) || amount} ${selectedCrypto} → ${sendWalletAddress.slice(0, 10)}...${txNum}`,
      });
      setExternalSendOpen(false);
      setSendAmount(""); setSendWalletAddress(""); setSendNetwork(""); setSelectedCrypto("");
      fetchData();
    } catch (err: any) {
      toast({ title: t('common.error'), description: err.message, variant: "destructive" });
    } finally {
      setExternalSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending": return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20 text-[10px] px-2 py-0.5"><Clock className="w-2.5 h-2.5 ml-1" />{t('wallet.pending')}</Badge>;
      case "approved": return <Badge className="bg-green-500/10 text-green-500 border-green-500/20 text-[10px] px-2 py-0.5"><CheckCircle2 className="w-2.5 h-2.5 ml-1" />{t('wallet.approved')}</Badge>;
      case "rejected": return <Badge className="bg-red-500/10 text-red-500 border-red-500/20 text-[10px] px-2 py-0.5"><XCircle className="w-2.5 h-2.5 ml-1" />{t('wallet.rejected')}</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: t('wallet.copied') });
  };

  const filteredBalances = useMemo(() => {
    // Always show these 5 default cryptos even with 0 balance
    const defaultCryptos = [
      { crypto_symbol: 'BTC', crypto_name: 'Bitcoin' },
      { crypto_symbol: 'ETH', crypto_name: 'Ethereum' },
      { crypto_symbol: 'USDT', crypto_name: 'Tether' },
      { crypto_symbol: 'SOL', crypto_name: 'Solana' },
      { crypto_symbol: 'BNB', crypto_name: 'BNB' },
    ];
    
    const merged: WalletBalance[] = defaultCryptos.map(dc => {
      const existing = balances.find(b => b.crypto_symbol === dc.crypto_symbol);
      return existing || { id: `default-${dc.crypto_symbol}`, crypto_symbol: dc.crypto_symbol, crypto_name: dc.crypto_name, balance: 0 };
    });
    
    // Add any other balances not in defaults
    balances.forEach(b => {
      if (!defaultCryptos.some(dc => dc.crypto_symbol === b.crypto_symbol)) {
        merged.push(b);
      }
    });

    let filtered = merged;
    if (hideSmallBalances) filtered = filtered.filter(b => b.balance * (priceMap[b.crypto_symbol] || 0) >= 1);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(b => b.crypto_symbol.toLowerCase().includes(q) || b.crypto_name.toLowerCase().includes(q) || (KNOWN_CRYPTO_META[b.crypto_symbol]?.nameEn || "").toLowerCase().includes(q));
    }
    
    // Sort by balance descending (highest balance first)
    filtered.sort((a, b) => b.balance - a.balance);
    
    return filtered;
  }, [balances, hideSmallBalances, searchQuery, priceMap]);

  const allHistory = useMemo(() => {
    const items: Array<{ type: 'deposit' | 'withdrawal' | 'transfer_out' | 'transfer_in'; date: string; symbol: string; amount: number | null; status: string; network: string; address?: string; txHash?: string | null; transactionNumber?: string | null; userAddress?: string; otherPartyName?: string }> = [];
    deposits.forEach(d => {
      const userAddr = userAddresses.find(a => a.network === d.network);
      items.push({ type: 'deposit', date: d.created_at, symbol: d.crypto_symbol, amount: d.amount, status: d.status, network: d.network, txHash: d.tx_hash, userAddress: userAddr?.address });
    });
    withdrawals.forEach(w => items.push({ type: 'withdrawal', date: w.created_at, symbol: w.crypto_symbol, amount: w.amount, status: w.status, network: w.network, address: w.wallet_address, txHash: w.tx_hash, transactionNumber: w.transaction_number }));
    internalTransfers.forEach(tr => {
      const isSender = tr.sender_id === user?.id;
      items.push({
        type: isSender ? 'transfer_out' : 'transfer_in',
        date: tr.created_at,
        symbol: tr.crypto_symbol,
        amount: tr.amount,
        status: 'approved',
        network: 'داخلي',
        transactionNumber: tr.transaction_number,
        otherPartyName: isSender ? (tr.recipient_name || 'مستخدم') : (tr.sender_name || 'مستخدم'),
      });
    });
    items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    return items;
  }, [deposits, withdrawals, internalTransfers, user?.id]);

  // Asset detail view
  if (selectedAssetDetail) {
    const b = balances.find(b => b.crypto_symbol === selectedAssetDetail);
    const meta = selectedAssetDetail ? KNOWN_CRYPTO_META[selectedAssetDetail] : null;
    const price = priceMap[selectedAssetDetail] || 0;
    const change = changeMap[selectedAssetDetail] || 0;
    const valueUsd = (b?.balance || 0) * price;
    const assetHistory = allHistory.filter(h => h.symbol === selectedAssetDetail);

    return (
      <div className="min-h-screen bg-background">
        <div className="sticky top-0 z-30 bg-background/95 backdrop-blur-md border-b border-border">
          <div className="flex items-center justify-between px-4 py-3">
            <button onClick={() => setSelectedAssetDetail(null)} className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors">
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm">{t('common.back') || 'رجوع'}</span>
            </button>
            <div className="flex items-center gap-2">
              <span className="text-lg">{meta?.icon}</span>
              <span className="font-semibold text-foreground">{selectedAssetDetail}</span>
            </div>
            <div className="w-16" />
          </div>
        </div>

        <div className="px-4 py-6">
          <div className="rounded-2xl bg-gradient-to-br from-primary/5 via-background to-primary/10 border border-border p-5 mb-6">
            <div className="text-center mb-4">
              <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center text-2xl font-bold" style={{ backgroundColor: `${meta?.color || 'hsl(var(--primary))'}15`, color: meta?.color || 'hsl(var(--primary))' }}>
                {meta?.icon || selectedAssetDetail.charAt(0)}
              </div>
              <p className="text-2xl font-bold text-foreground" dir="ltr">${price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              <div className={`flex items-center justify-center gap-1 mt-1 ${change >= 0 ? "text-green-500" : "text-red-500"}`}>
                {change >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                <span className="text-sm font-medium">{change >= 0 ? "+" : ""}{change.toFixed(2)}%</span>
                <span className="text-xs text-muted-foreground mr-1">24h</span>
              </div>
            </div>
            <div className="bg-background/60 rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{t('wallet.available') || 'المتاح'}</span>
                <span className="text-sm font-semibold text-foreground" dir="ltr">{b?.balance?.toFixed(8)?.replace(/\.?0+$/, '') || '0'} {selectedAssetDetail}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{t('wallet.value') || 'القيمة'}</span>
                <span className="text-sm font-medium text-muted-foreground" dir="ltr">≈ ${valueUsd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 mb-6">
            <Button
              onClick={() => { setSelectedReceiveCrypto(selectedAssetDetail); setReceiveOpen(true); }}
              className="h-12 rounded-xl bg-green-500/10 hover:bg-green-500/20 text-green-500 border border-green-500/20 font-semibold gap-1 text-xs"
              variant="ghost"
            >
              <ArrowDownLeft className="w-4 h-4" />
              {t('wallet.receive') || 'استقبال'}
            </Button>
            <Button
              onClick={() => { setSwapFrom(selectedAssetDetail); setSwapOpen(true); }}
              disabled={!b || b.balance <= 0}
              className="h-12 rounded-xl bg-accent/10 hover:bg-accent/20 text-accent-foreground border border-accent/20 font-semibold gap-1 text-xs"
              variant="ghost"
            >
              <ArrowLeftRight className="w-4 h-4" />
              {t('wallet.swap') || 'استبدال'}
            </Button>
            <Button
              onClick={() => { setSelectedCrypto(selectedAssetDetail); setSendModeOpen(true); }}
              disabled={!b || b.balance <= 0}
              className="h-12 rounded-xl bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20 font-semibold gap-1 text-xs"
              variant="ghost"
            >
              <ArrowUpRight className="w-4 h-4" />
              {t('wallet.send') || 'إرسال'}
            </Button>
          </div>

          <h3 className="text-sm font-semibold text-foreground mb-3">{t('wallet.history') || 'السجل'}</h3>
          {assetHistory.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-sm text-muted-foreground">{t('wallet.noHistory') || 'لا توجد عمليات سابقة'}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {assetHistory.map((h, i) => {
                const isExpanded = expandedHistoryIndex === i + 10000;
                const meta = KNOWN_CRYPTO_META[h.symbol];
                return (
                  <div
                    key={i}
                    className="rounded-xl bg-secondary/40 border border-border/50 overflow-hidden transition-all cursor-pointer"
                    onClick={() => setExpandedHistoryIndex(isExpanded ? null : i + 10000)}
                  >
                    <div className="flex items-center justify-between p-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                          h.type === 'deposit' || h.type === 'transfer_in' ? 'bg-green-500/10' : 'bg-red-500/10'
                        }`}>
                          {h.type === 'deposit' ? <ArrowDownLeft className="w-4 h-4 text-green-500" /> 
                           : h.type === 'transfer_in' ? <ArrowDownLeft className="w-4 h-4 text-green-500" />
                           : h.type === 'transfer_out' ? <ArrowUpRight className="w-4 h-4 text-primary" />
                           : <ArrowUpRight className="w-4 h-4 text-red-500" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {h.type === 'deposit' ? (t('wallet.depositWord') || 'إيداع') 
                             : h.type === 'transfer_in' ? 'تحويل وارد'
                             : h.type === 'transfer_out' ? 'تحويل صادر'
                             : (t('wallet.withdrawWord') || 'سحب')}
                          </p>
                          <p className="text-[10px] text-muted-foreground">
                            {h.otherPartyName ? `${h.type === 'transfer_in' ? 'من' : 'إلى'}: ${h.otherPartyName} • ` : ''}
                            {new Date(h.date).toLocaleDateString('ar', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                      <div className="text-left" dir="ltr">
                        <p className={`text-sm font-semibold ${
                          h.type === 'deposit' || h.type === 'transfer_in' ? 'text-green-500' 
                          : h.type === 'transfer_out' ? 'text-primary' 
                          : 'text-red-500'
                        }`}>
                          {h.type === 'deposit' || h.type === 'transfer_in' ? '+' : '-'}{h.amount ?? '—'}
                        </p>
                        {getStatusBadge(h.status)}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="px-3 pb-3 pt-1 border-t border-border/50 space-y-2 animate-fade-in">
                        <div className="grid grid-cols-2 gap-2">
                          <div className="bg-secondary/50 rounded-lg p-2">
                            <p className="text-[10px] text-muted-foreground mb-0.5">{t('wallet.currency') || 'العملة'}</p>
                            <div className="flex items-center gap-1">
                              <span className="text-sm">{meta?.icon || '🪙'}</span>
                              <span className="text-xs font-semibold text-foreground">{h.symbol}</span>
                            </div>
                          </div>
                          <div className="bg-secondary/50 rounded-lg p-2">
                            <p className="text-[10px] text-muted-foreground mb-0.5">الشبكة</p>
                            <p className="text-xs font-semibold text-foreground">{h.network}</p>
                          </div>
                        </div>

                        {h.transactionNumber && (
                          <div className="bg-secondary/50 rounded-lg p-2">
                            <p className="text-[10px] text-muted-foreground mb-0.5">رقم العملية</p>
                            <div className="flex items-center justify-between">
                              <p className="text-xs font-mono text-primary font-semibold">#{h.transactionNumber}</p>
                              <button onClick={(e) => { e.stopPropagation(); copyToClipboard(h.transactionNumber!); }} className="text-muted-foreground hover:text-foreground"><Copy className="w-3 h-3" /></button>
                            </div>
                          </div>
                        )}

                        {h.type === 'deposit' && h.userAddress && (
                          <div className="bg-secondary/50 rounded-lg p-2">
                            <p className="text-[10px] text-muted-foreground mb-0.5">عنوان الاستقبال (عنوانك)</p>
                            <div className="flex items-center justify-between gap-1">
                              <p className="text-[10px] font-mono text-foreground break-all" dir="ltr">{h.userAddress}</p>
                              <button onClick={(e) => { e.stopPropagation(); copyToClipboard(h.userAddress!); }} className="text-muted-foreground hover:text-foreground shrink-0"><Copy className="w-3 h-3" /></button>
                            </div>
                          </div>
                        )}

                        {h.type === 'withdrawal' && h.address && (
                          <div className="bg-secondary/50 rounded-lg p-2">
                            <p className="text-[10px] text-muted-foreground mb-0.5">أُرسلت إلى</p>
                            <div className="flex items-center justify-between gap-1">
                              <p className="text-[10px] font-mono text-foreground break-all" dir="ltr">{h.address}</p>
                              <button onClick={(e) => { e.stopPropagation(); copyToClipboard(h.address!); }} className="text-muted-foreground hover:text-foreground shrink-0"><Copy className="w-3 h-3" /></button>
                            </div>
                          </div>
                        )}

                        {h.txHash && (
                          <div className="bg-secondary/50 rounded-lg p-2">
                            <p className="text-[10px] text-muted-foreground mb-0.5">TxID</p>
                            <div className="flex items-center justify-between gap-1">
                              <p className="text-[10px] font-mono text-primary break-all" dir="ltr">{h.txHash}</p>
                              <button onClick={(e) => { e.stopPropagation(); copyToClipboard(h.txHash!); }} className="text-muted-foreground hover:text-foreground shrink-0"><Copy className="w-3 h-3" /></button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
        <div className="animate-spin text-primary">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" /><path d="M3 5v14a2 2 0 0 0 2 2h16v-5" /><path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
          </svg>
        </div>
        <p className="text-sm text-muted-foreground animate-pulse">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 pb-24">
        {(userName || userNumericId) && (
          <div className="mx-4 mt-4 mb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              {userName && (
                <p className="text-sm font-semibold text-foreground"><span className="text-muted-foreground">{t('wallet.userName')}:</span> <span className="text-primary">{userName}</span></p>
              )}
              {userNumericId && (
                <button onClick={() => { navigator.clipboard.writeText(userNumericId); toast({ title: t('wallet.copied') || 'تم النسخ ✅' }); }} className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 bg-secondary/50 px-2 py-1 rounded-md font-mono" dir="ltr">
                  <Copy className="w-3 h-3" />
                  ID: {userNumericId}
                </button>
              )}
            </div>
          </div>
        )}
        <div className={`mx-4 ${userName ? 'mt-1' : 'mt-4'} rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-background border border-primary/10 p-5 relative overflow-hidden`}>
          <div className="absolute top-0 left-0 w-32 h-32 bg-primary/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-24 h-24 bg-primary/5 rounded-full translate-x-1/3 translate-y-1/3" />
          
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground font-medium">{t('wallet.totalBalance') || 'إجمالي الرصيد'}</span>
                <button onClick={() => setHideBalance(!hideBalance)} className="text-muted-foreground hover:text-foreground transition-colors">
                  {hideBalance ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </div>
              <button onClick={fetchData} className="text-muted-foreground hover:text-foreground transition-colors p-1.5 rounded-lg hover:bg-secondary">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            
            <div dir="ltr" className="mb-1">
              <h1 className="text-3xl font-bold text-foreground tracking-tight">
                {hideBalance ? "••••••" : `$${totalUsd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              </h1>
            </div>
            
            <p className="text-xs text-muted-foreground mb-5">
              {hideBalance ? "••••" : `≈ ${(totalUsd / (priceMap["BTC"] || 67000)).toFixed(8)} BTC`}
            </p>

            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setReceiveOpen(true)}
                className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-xl bg-green-500/10 hover:bg-green-500/15 border border-green-500/20 transition-all active:scale-[0.98]"
              >
                <div className="w-8 h-8 rounded-full bg-green-500/15 flex items-center justify-center">
                  <ArrowDownLeft className="w-4 h-4 text-green-500" />
                </div>
                <span className="text-xs font-semibold text-green-500">{t('wallet.receive') || 'استقبال'}</span>
              </button>
              <button
                onClick={() => setSwapOpen(true)}
                disabled={balances.length < 1}
                className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-xl bg-accent/10 hover:bg-accent/15 border border-accent/20 transition-all active:scale-[0.98] disabled:opacity-40"
              >
                <div className="w-8 h-8 rounded-full bg-accent/15 flex items-center justify-center">
                  <ArrowLeftRight className="w-4 h-4 text-accent-foreground" />
                </div>
                <span className="text-xs font-semibold text-accent-foreground">{t('wallet.swap') || 'استبدال'}</span>
              </button>
              <button
                onClick={() => setSendModeOpen(true)}
                disabled={balances.length === 0}
                className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-xl bg-primary/10 hover:bg-primary/15 border border-primary/20 transition-all active:scale-[0.98] disabled:opacity-40"
              >
                <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center">
                  <ArrowUpRight className="w-4 h-4 text-primary" />
                </div>
                <span className="text-xs font-semibold text-primary">{t('wallet.send') || 'إرسال'}</span>
              </button>
            </div>
          </div>
        </div>

        <div className="mx-4 mt-5 mb-3 flex gap-1 p-1 rounded-xl bg-secondary/60">
          {[
            { key: "crypto" as const, label: t('wallet.assets') || 'الأصول' },
            { key: "history" as const, label: t('wallet.history') || 'السجل', count: deposits.filter(d => d.status === "pending").length + withdrawals.filter(w => w.status === "pending").length },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.key
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab.label}
              {tab.count && tab.count > 0 ? (
                <span className="bg-primary text-primary-foreground text-[10px] font-bold rounded-full w-4.5 h-4.5 min-w-[18px] flex items-center justify-center px-1">{tab.count}</span>
              ) : null}
            </button>
          ))}
        </div>

        {activeTab === "crypto" && (
          <div>
            <div className="px-4 pb-2 flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder={t('wallet.search') || 'بحث...'} className="bg-secondary/60 border-0 pr-9 h-9 text-sm rounded-xl placeholder:text-muted-foreground" />
              </div>
              <button onClick={() => setHideSmallBalances(!hideSmallBalances)} className={`text-xs px-3 py-2 rounded-lg transition-colors whitespace-nowrap ${hideSmallBalances ? "bg-primary/15 text-primary" : "bg-secondary/60 text-muted-foreground"}`}>
                {t('wallet.hideSmall') || 'إخفاء الصغيرة'}
              </button>
            </div>
            {filteredBalances.length === 0 ? (
              <div className="px-4 py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-secondary/60 flex items-center justify-center mx-auto mb-4">
                  <Search className="w-7 h-7 text-muted-foreground/50" />
                </div>
                <p className="text-muted-foreground text-sm font-medium">{balances.length === 0 ? (t('wallet.noBalances') || 'لا توجد أرصدة') : (t('wallet.noResults') || 'لا توجد نتائج')}</p>
                {balances.length === 0 && <p className="text-muted-foreground/60 text-xs mt-2">{t('wallet.noBalancesHint') || 'قم بالاستقبال لبدء التداول'}</p>}
              </div>
            ) : (
              <div className="mx-4 space-y-2">
                {filteredBalances.map(b => {
                  const meta = KNOWN_CRYPTO_META[b.crypto_symbol];
                  const price = priceMap[b.crypto_symbol] || 0;
                  const change = changeMap[b.crypto_symbol] || 0;
                  const valueUsd = b.balance * price;
                  return (
                    <button
                      key={b.id}
                      onClick={() => setSelectedAssetDetail(b.crypto_symbol)}
                      className="w-full flex items-center justify-between p-3.5 rounded-xl bg-secondary/30 hover:bg-secondary/50 border border-border/50 transition-all active:scale-[0.99]"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-base font-bold shrink-0" style={{ backgroundColor: `${meta?.color || 'hsl(var(--primary))'}15`, color: meta?.color || 'hsl(var(--primary))' }}>
                          {meta?.icon || b.crypto_symbol.charAt(0)}
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-foreground leading-tight">{b.crypto_symbol}</p>
                          <p className="text-[11px] text-muted-foreground">{meta?.nameEn || b.crypto_name}</p>
                        </div>
                      </div>
                      <div className="text-left" dir="ltr">
                        <p className="text-sm font-semibold text-foreground leading-tight">
                          {hideBalance ? "••••" : b.balance.toFixed(8).replace(/\.?0+$/, '')}
                        </p>
                        <div className="flex items-center gap-2 justify-end">
                          <span className="text-[11px] text-muted-foreground">
                            {hideBalance ? "••••" : `$${valueUsd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                          </span>
                          {!hideBalance && (
                            <span className={`text-[10px] font-medium ${change >= 0 ? "text-green-500" : "text-red-500"}`}>
                              {change >= 0 ? "+" : ""}{change.toFixed(1)}%
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === "history" && (
          <div className="mx-4">
            {allHistory.length === 0 ? (
              <div className="py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-secondary/60 flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-7 h-7 text-muted-foreground/50" />
                </div>
                <p className="text-muted-foreground text-sm font-medium">{t('wallet.noHistory') || 'لا توجد عمليات سابقة'}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {allHistory.map((h, i) => {
                  const meta = KNOWN_CRYPTO_META[h.symbol];
                  const isExpanded = expandedHistoryIndex === i;
                  return (
                    <div
                      key={i}
                      className="rounded-xl bg-secondary/30 border border-border/50 overflow-hidden transition-all cursor-pointer"
                      onClick={() => setExpandedHistoryIndex(isExpanded ? null : i)}
                    >
                      <div className="flex items-center justify-between p-3.5">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            h.type === 'deposit' || h.type === 'transfer_in' ? 'bg-green-500/10' 
                            : h.type === 'transfer_out' ? 'bg-primary/10' 
                            : 'bg-red-500/10'
                          }`}>
                            {h.type === 'deposit' ? <ArrowDownLeft className="w-4.5 h-4.5 text-green-500" /> 
                             : h.type === 'transfer_in' ? <ArrowDownLeft className="w-4.5 h-4.5 text-green-500" />
                             : h.type === 'transfer_out' ? <ArrowUpRight className="w-4.5 h-4.5 text-primary" />
                             : <ArrowUpRight className="w-4.5 h-4.5 text-red-500" />}
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-semibold text-foreground">
                                {h.type === 'deposit' ? (t('wallet.depositWord') || 'إيداع') 
                                 : h.type === 'transfer_in' ? 'تحويل وارد'
                                 : h.type === 'transfer_out' ? 'تحويل صادر'
                                 : (t('wallet.withdrawWord') || 'سحب')}
                              </span>
                              <span className="text-xs text-muted-foreground">{h.symbol}</span>
                            </div>
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              {h.otherPartyName ? `${h.type === 'transfer_in' ? 'من' : 'إلى'}: ${h.otherPartyName} • ` : ''}
                              {h.network} • {new Date(h.date).toLocaleDateString('ar', { month: 'short', day: 'numeric' })}
                            </p>
                          </div>
                        </div>
                        <div className="text-left flex flex-col items-end gap-1" dir="ltr">
                          <p className={`text-sm font-semibold ${
                            h.type === 'deposit' || h.type === 'transfer_in' ? 'text-green-500' 
                            : h.type === 'transfer_out' ? 'text-primary' 
                            : 'text-red-500'
                          }`}>
                            {h.type === 'deposit' || h.type === 'transfer_in' ? '+' : '-'}{h.amount ?? '—'}
                          </p>
                          {getStatusBadge(h.status)}
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="px-4 pb-4 pt-1 border-t border-border/50 space-y-2.5 animate-fade-in">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="bg-secondary/50 rounded-lg p-2.5">
                              <p className="text-[10px] text-muted-foreground mb-0.5">{t('wallet.currency') || 'العملة'}</p>
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm">{meta?.icon || '🪙'}</span>
                                <span className="text-xs font-semibold text-foreground">{h.symbol}</span>
                              </div>
                            </div>
                            <div className="bg-secondary/50 rounded-lg p-2.5">
                              <p className="text-[10px] text-muted-foreground mb-0.5">{t('wallet.quantity') || 'الكمية'}</p>
                              <p className="text-xs font-semibold text-foreground" dir="ltr">{h.amount ?? '—'} {h.symbol}</p>
                            </div>
                            <div className="bg-secondary/50 rounded-lg p-2.5">
                              <p className="text-[10px] text-muted-foreground mb-0.5">الشبكة</p>
                              <p className="text-xs font-semibold text-foreground">{h.network}</p>
                            </div>
                            <div className="bg-secondary/50 rounded-lg p-2.5">
                              <p className="text-[10px] text-muted-foreground mb-0.5">الحالة</p>
                              {getStatusBadge(h.status)}
                            </div>
                          </div>

                          {h.transactionNumber && (
                            <div className="bg-secondary/50 rounded-lg p-2.5">
                              <p className="text-[10px] text-muted-foreground mb-0.5">رقم العملية</p>
                              <div className="flex items-center justify-between">
                                <p className="text-xs font-mono text-primary font-semibold">#{h.transactionNumber}</p>
                                <button onClick={(e) => { e.stopPropagation(); copyToClipboard(h.transactionNumber!); }} className="text-muted-foreground hover:text-foreground"><Copy className="w-3.5 h-3.5" /></button>
                              </div>
                            </div>
                          )}

                          {h.type === 'deposit' && h.userAddress && (
                            <div className="bg-secondary/50 rounded-lg p-2.5">
                              <p className="text-[10px] text-muted-foreground mb-0.5">عنوان الاستقبال (عنوانك)</p>
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-[11px] font-mono text-foreground break-all leading-relaxed" dir="ltr">{h.userAddress}</p>
                                <button onClick={(e) => { e.stopPropagation(); copyToClipboard(h.userAddress!); }} className="text-muted-foreground hover:text-foreground shrink-0"><Copy className="w-3.5 h-3.5" /></button>
                              </div>
                            </div>
                          )}

                          {(h.type === 'transfer_in' || h.type === 'transfer_out') && h.otherPartyName && (
                            <div className="bg-secondary/50 rounded-lg p-2.5">
                              <p className="text-[10px] text-muted-foreground mb-0.5">{h.type === 'transfer_in' ? 'من' : 'إلى'}</p>
                              <p className="text-xs font-semibold text-foreground">{h.otherPartyName}</p>
                            </div>
                          )}

                          {h.type === 'withdrawal' && h.address && (
                            <div className="bg-secondary/50 rounded-lg p-2.5">
                              <p className="text-[10px] text-muted-foreground mb-0.5">أُرسلت إلى</p>
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-[11px] font-mono text-foreground break-all leading-relaxed" dir="ltr">{h.address}</p>
                                <button onClick={(e) => { e.stopPropagation(); copyToClipboard(h.address!); }} className="text-muted-foreground hover:text-foreground shrink-0"><Copy className="w-3.5 h-3.5" /></button>
                              </div>
                            </div>
                          )}

                          {h.txHash && (
                            <div className="bg-secondary/50 rounded-lg p-2.5">
                              <p className="text-[10px] text-muted-foreground mb-0.5">TxID (معرّف المعاملة)</p>
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-[11px] font-mono text-primary break-all leading-relaxed" dir="ltr">{h.txHash}</p>
                                <button onClick={(e) => { e.stopPropagation(); copyToClipboard(h.txHash!); }} className="text-muted-foreground hover:text-foreground shrink-0"><Copy className="w-3.5 h-3.5" /></button>
                              </div>
                            </div>
                          )}

                          <div className="bg-secondary/50 rounded-lg p-2.5">
                            <p className="text-[10px] text-muted-foreground mb-0.5">التاريخ</p>
                            <p className="text-xs text-foreground">{new Date(h.date).toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Send (Internal Transfer) Dialog */}
      <Dialog open={sendOpen} onOpenChange={(open) => { setSendOpen(open); if (!open) { setSendAmount(""); setRecipientId(""); setSelectedCrypto(""); } }}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden rounded-2xl">
          <div className="bg-gradient-to-b from-primary/5 to-transparent px-5 pt-5 pb-4">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <ArrowUpRight className="w-4 h-4 text-primary" />
                </div>
                {t('wallet.internalTransfer')}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">{t('wallet.internalTransferDesc')}</DialogDescription>
            </DialogHeader>
          </div>
          <div className="px-5 pb-5 space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-medium">{t('wallet.currency')}</Label>
              <Select value={selectedCrypto} onValueChange={setSelectedCrypto}>
                <SelectTrigger className="h-11 rounded-xl bg-secondary/60 border-0"><SelectValue placeholder={t('wallet.selectCurrency')} /></SelectTrigger>
                <SelectContent>
                  {balances.filter(b => b.balance > 0).map(b => {
                    const meta = KNOWN_CRYPTO_META[b.crypto_symbol];
                    return (
                      <SelectItem key={b.crypto_symbol} value={b.crypto_symbol}>
                        <div className="flex items-center gap-2">
                          <span>{meta?.icon}</span>
                          <span>{meta?.nameEn || b.crypto_name}</span>
                          <span className="text-muted-foreground text-xs">({b.balance})</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium">{t('wallet.recipientId')}</Label>
              <Input
                value={recipientId}
                onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 16); setRecipientId(v); }}
                placeholder="0000000000000000"
                dir="ltr"
                className="bg-secondary/60 border-0 h-11 rounded-xl font-mono text-center tracking-wider"
                maxLength={16}
              />
              {lookingUpRecipient && (
                <p className="text-[10px] text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />{t('wallet.searching')}</p>
              )}
              {!lookingUpRecipient && recipientId.length === 16 && recipientName && (
                <div className="flex items-center gap-2 mt-1">
                  {recipientAvatar ? (
                    <img src={recipientAvatar} alt={recipientName} className="w-8 h-8 rounded-full object-cover border-2 border-primary/30" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-bold">
                      {recipientName.charAt(0)}
                    </div>
                  )}
                  <p className="text-[11px] text-primary font-semibold flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />{t('wallet.recipient')} {recipientName}</p>
                </div>
              )}
              {!lookingUpRecipient && recipientId.length === 16 && !recipientName && (
                <p className="text-[11px] text-destructive flex items-center gap-1"><XCircle className="w-3 h-3" />{t('wallet.idNotFound')}</p>
              )}
              {recipientId.length < 16 && (
                <p className="text-[10px] text-muted-foreground">{t('wallet.recipientIdHint')}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium">{t('wallet.quantity')}</Label>
              <div className="relative">
                <Input type="number" value={sendAmount} onChange={e => setSendAmount(e.target.value)} placeholder="0.00" dir="ltr" className="bg-secondary/60 border-0 h-11 rounded-xl font-mono" />
                {selectedBalance && (
                  <button onClick={() => setSendAmount(selectedBalance.balance.toString())} className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-primary font-semibold bg-primary/10 px-2 py-0.5 rounded hover:bg-primary/15">{t('wallet.all')}</button>
                )}
              </div>
              {selectedBalance && <p className="text-[11px] text-muted-foreground">{t('wallet.available')}: {selectedBalance.balance} {selectedCrypto}</p>}

              {/* Fee breakdown for internal transfer */}
              {selectedCrypto && sendAmount && parseFloat(sendAmount) > 0 && (() => {
                const meta = KNOWN_CRYPTO_META[selectedCrypto];
                const networks = meta?.networks || [];
                const avgFee = networks.length > 0 ? networks.reduce((s, n) => s + n.fee, 0) / networks.length : 0;
                const cheapestNetwork = networks.length > 0 ? networks.reduce((a, b) => a.fee < b.fee ? a : b) : null;
                const fee = cheapestNetwork?.fee || avgFee;
                return fee > 0 ? (
                  <div className="bg-secondary/60 rounded-xl p-3 space-y-1.5 text-xs">
                    <div className="flex justify-between"><span className="text-muted-foreground">المبلغ الكلي</span><span className="text-foreground font-mono">{sendAmount} {selectedCrypto}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">رسوم الشبكة (تقريبي)</span><span className="text-destructive font-mono">~${fee.toFixed(2)}</span></div>
                    <div className="border-t border-border pt-1.5 flex justify-between"><span className="text-muted-foreground font-medium">يستلم المستلم</span><span className="text-primary font-semibold font-mono">المبلغ - الرسوم</span></div>
                    {cheapestNetwork && <p className="text-[10px] text-muted-foreground">الشبكة: {cheapestNetwork.name}</p>}
                  </div>
                ) : null;
              })()}
            </div>

            <Button
              onClick={handleSend}
              disabled={submitting || !selectedCrypto || !recipientId || recipientId.length !== 16 || !recipientName || !sendAmount || parseFloat(sendAmount) <= 0}
              className="w-full h-12 rounded-xl font-semibold text-sm"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : null}
              {submitting ? t('wallet.transferring') : t('wallet.confirmTransfer')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Mode Chooser */}
      <Dialog open={sendModeOpen} onOpenChange={setSendModeOpen}>
        <DialogContent className="sm:max-w-sm p-0 overflow-hidden rounded-2xl">
          <div className="bg-gradient-to-b from-primary/5 to-transparent px-5 pt-5 pb-4">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <ArrowUpRight className="w-4 h-4 text-primary" />
                </div>
                {t('wallet.chooseSendMethod')}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">{t('wallet.chooseSendMethodDesc')}</DialogDescription>
            </DialogHeader>
          </div>
          <div className="px-5 pb-5 space-y-3">
            <button
              onClick={async () => {
                setSendModeOpen(false);
                if (hasBiometric) {
                  setBiometricVerifying(true);
                  try {
                    const result = await loginBiometric();
                    if (result.success) {
                      setSendOpen(true);
                    } else {
                      toast({ title: t('common.error'), description: 'فشل التحقق البيومتري', variant: "destructive" });
                    }
                  } catch {
                    toast({ title: t('common.error'), description: 'فشل التحقق البيومتري', variant: "destructive" });
                  } finally {
                    setBiometricVerifying(false);
                  }
                } else {
                  setSendOpen(true);
                }
              }}
              className="w-full flex items-center gap-4 p-4 rounded-xl border border-primary/20 bg-primary/5 hover:bg-primary/10 transition-all"
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-foreground">{t('wallet.internalTransferTitle')}</p>
                <p className="text-xs text-muted-foreground">{t('wallet.internalTransferHint')}</p>
              </div>
            </button>
            <button
             onClick={async () => {
               setSendModeOpen(false);
               if (hasBiometric) {
                 setBiometricVerifying(true);
                 try {
                   const result = await loginBiometric();
                   if (result.success) {
                      setWeb3SendOpen(true);

                   } else {
                     toast({ title: t('common.error'), description: 'فشل التحقق البيومتري', variant: "destructive" });
                   }
                 } catch {
                   toast({ title: t('common.error'), description: 'فشل التحقق البيومتري', variant: "destructive" });
                 } finally {
                   setBiometricVerifying(false);
                 }
               } else {
                 setWeb3SendOpen(true);
               }
             }}
              className="w-full flex items-center gap-4 p-4 rounded-xl border border-border bg-secondary/30 hover:bg-secondary/50 transition-all"
            >
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center shrink-0">
                <Globe className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-foreground">{t('wallet.externalSend')}</p>
                <p className="text-xs text-muted-foreground">{t('wallet.externalSendHint')}</p>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Direct on-chain send via user's connected wallet (MetaMask etc.) */}
      <Web3SendDialog open={web3SendOpen} onOpenChange={setWeb3SendOpen} />

      {/* External Send Drawer (legacy custodial — disabled, kept for back-compat) */}

      <Drawer open={externalSendOpen} onOpenChange={(open) => { setExternalSendOpen(open); if (!open) { setSendAmount(""); setSendWalletAddress(""); setSendNetwork(""); setSelectedCrypto(""); } }}>
        <DrawerContent className="max-h-[90vh]">
          <DrawerHeader className="pb-2">
            <DrawerTitle className="flex items-center gap-2 text-lg">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Globe className="w-4 h-4 text-primary" />
              </div>
              {t('wallet.externalSendTitle') || 'إرسال خارجي'}
            </DrawerTitle>
            <p className="text-xs text-muted-foreground">
              {t('wallet.externalSendDesc') || 'إرسال عملة رقمية لعنوان خارجي من رصيدك'}
            </p>
          </DrawerHeader>
          <div className="px-4 pb-6 space-y-4 overflow-y-auto max-h-[70vh]">
            <div className="space-y-2">
              <Label className="text-xs font-medium">{t('wallet.selectCoin')}</Label>
              <Select value={selectedCrypto} onValueChange={(v) => { setSelectedCrypto(v); setSendNetwork(""); }}>
                <SelectTrigger className="h-11 rounded-xl bg-secondary/60 border-0"><SelectValue placeholder={t('wallet.selectCoin')} /></SelectTrigger>
                <SelectContent>
                  {balances.filter(b => b.balance > 0).map(b => {
                    const meta = KNOWN_CRYPTO_META[b.crypto_symbol];
                    return (
                      <SelectItem key={b.crypto_symbol} value={b.crypto_symbol}>
                        <div className="flex items-center gap-2">
                          <span>{meta?.icon}</span>
                          <span>{meta?.nameEn || b.crypto_name}</span>
                          <span className="text-muted-foreground text-xs">({b.balance})</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {selectedCrypto && (
              <div className="bg-secondary/60 rounded-xl p-3 text-xs text-muted-foreground flex items-center gap-2">
                <RefreshCw className="w-3.5 h-3.5 shrink-0" />
                <span>شبكة الإرسال إلى Binance تُحدد تلقائياً بناءً على موقع الرصيد</span>
              </div>
            )}

            {selectedCrypto && KNOWN_CRYPTO_META[selectedCrypto]?.networks && (
              <div className="space-y-2">
                <Label className="text-xs font-medium">شبكة الاستلام (وجهة المستلم)</Label>
                <div className="grid grid-cols-2 gap-2">
                  {KNOWN_CRYPTO_META[selectedCrypto].networks.map(n => (
                    <button
                      key={n.name}
                      onClick={() => setSendNetwork(n.name)}
                      className={`p-3 rounded-xl border text-center transition-all ${
                        sendNetwork === n.name
                          ? 'border-primary bg-primary/10 ring-1 ring-primary'
                          : 'border-border bg-secondary/40 hover:bg-secondary/60'
                      }`}
                    >
                      <p className="text-xs font-semibold text-foreground">{n.name}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">~${n.fee} • ⏱ {n.time}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-xs font-medium">عنوان المستلم</Label>
              <Input
                value={sendWalletAddress}
                onChange={e => setSendWalletAddress(e.target.value.trim())}
                placeholder="0x... / T... / ..."
                dir="ltr"
                className="bg-secondary/60 border-0 h-11 rounded-xl font-mono text-xs"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium">{t('wallet.quantityLabel')}</Label>
              <div className="relative">
                <Input type="number" value={sendAmount} onChange={e => setSendAmount(e.target.value)} placeholder="0.00" dir="ltr" className="bg-secondary/60 border-0 h-11 rounded-xl font-mono" />
                {selectedCrypto && (() => {
                  const bal = balances.find(b => b.crypto_symbol === selectedCrypto);
                  return bal ? (
                    <button onClick={() => setSendAmount(bal.balance.toString())} className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-primary font-semibold bg-primary/10 px-2 py-0.5 rounded hover:bg-primary/15">{t('wallet.all')}</button>
                  ) : null;
                })()}
              </div>
              {selectedCrypto && (() => {
                const bal = balances.find(b => b.crypto_symbol === selectedCrypto);
                return bal ? <p className="text-[11px] text-muted-foreground">{t('wallet.available')}: {bal.balance} {selectedCrypto}</p> : null;
              })()}
            </div>

            {/* Fee info */}
            {selectedCrypto && sendNetwork && sendAmount && parseFloat(sendAmount) > 0 && (() => {
              const net = KNOWN_CRYPTO_META[selectedCrypto]?.networks?.find(n => n.name === sendNetwork);
              const fee = net?.fee || 0;
              return (
                <div className="bg-secondary/60 rounded-xl p-3 space-y-1.5 text-xs">
                  <div className="flex justify-between"><span className="text-muted-foreground">المبلغ</span><span className="text-foreground font-mono">{sendAmount} {selectedCrypto}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">رسوم الشبكة (تقريبي)</span><span className="text-destructive font-mono">~${fee.toFixed(2)}</span></div>
                  <div className="border-t border-border pt-1.5 flex justify-between"><span className="text-muted-foreground font-medium">يصل للمستلم</span><span className="text-primary font-semibold font-mono">المبلغ - الرسوم</span></div>
                </div>
              );
            })()}

            <Button
              onClick={handleExternalSend}
              disabled={!selectedCrypto || !sendWalletAddress || !sendNetwork || !sendAmount || parseFloat(sendAmount) <= 0 || externalSubmitting}
              className="w-full h-14 rounded-2xl text-base font-bold"
            >
              {externalSubmitting ? (
                <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" />جاري الإرسال...</span>
              ) : `إرسال ${selectedCrypto || ''}`}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      <Drawer open={receiveOpen} onOpenChange={(open) => { setReceiveOpen(open); if (!open) { setSelectedReceiveNetwork(""); setSelectedReceiveCrypto(""); } }}>
        <DrawerContent className="max-h-[90vh]">
          <div className="mx-auto w-full max-w-md overflow-y-auto">
            <div className="sticky top-0 bg-background z-10 px-4 pt-2 pb-3 border-b border-border">
              <DrawerHeader className="p-0">
                <DrawerTitle className="text-lg font-bold text-center flex items-center justify-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-green-500/10 flex items-center justify-center">
                    <ArrowDownLeft className="w-3.5 h-3.5 text-green-500" />
                  </div>
                  {t('wallet.depositCrypto') || 'استقبال عملة'}
                </DrawerTitle>
              </DrawerHeader>
            </div>
            <div className="px-4 pb-8 space-y-5 pt-4">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground font-medium">{t('wallet.currency') || 'العملة'}</Label>
                <Select value={selectedReceiveCrypto} onValueChange={(v) => { setSelectedReceiveCrypto(v); setSelectedReceiveNetwork(""); }}>
                  <SelectTrigger className="h-12 bg-secondary/60 border-0 rounded-xl"><SelectValue placeholder={t('wallet.selectCrypto') || 'اختر العملة'} /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(KNOWN_CRYPTO_META).map(([symbol, meta]) => (
                      <SelectItem key={symbol} value={symbol}>
                        <div className="flex items-center gap-2">
                          <span className="text-base">{meta.icon}</span>
                          <span>{meta.nameEn}</span>
                          <span className="text-muted-foreground text-xs">({symbol})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedReceiveCrypto && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground font-medium">{t('wallet.selectNetwork') || 'اختر الشبكة'}</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {(KNOWN_CRYPTO_META[selectedReceiveCrypto]?.networks || []).map(n => (
                      <button key={n.name} onClick={() => setSelectedReceiveNetwork(n.name)} className={`p-3 rounded-xl border text-right transition-all ${selectedReceiveNetwork === n.name ? "border-green-500 bg-green-500/5 shadow-sm" : "border-border bg-secondary/30 hover:border-green-500/50"}`}>
                        <p className="text-sm font-medium text-foreground truncate">{n.name}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">⏱ {t('wallet.receiveTime')}: 6 {t('wallet.minutes') || 'min'}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {(() => {
                if (!selectedReceiveNetwork) return null;
                
                const userAddr = userAddresses.find(a => a.network === selectedReceiveNetwork);
                const displayAddress = userAddr?.address;

                const generateUniqueAddress = async () => {
                  setGeneratingAddress(true);
                  try {
                    const { data, error } = await supabase.functions.invoke("generate-address", {
                      body: { network: selectedReceiveNetwork },
                    });
                    if (error) throw error;
                    if (data?.address) {
                      setUserAddresses(prev => [...prev, { id: crypto.randomUUID(), blockchain: '', network: selectedReceiveNetwork, address: data.address }]);
                      toast({ title: "تم التوليد ✅", description: `تم إنشاء عنوان فريد لك على ${selectedReceiveNetwork}` });
                    } else if (data?.error) {
                      toast({ title: "خطأ", description: data.error, variant: "destructive" });
                    }
                  } catch (e: any) {
                    toast({ title: "خطأ", description: e.message || "فشل في توليد العنوان", variant: "destructive" });
                  } finally {
                    setGeneratingAddress(false);
                  }
                };

                if (!displayAddress) {
                  return (
                    <div className="p-5 rounded-xl bg-secondary text-center space-y-3">
                      <p className="text-sm text-muted-foreground">جاري إنشاء عنوانك الخاص على <strong>{selectedReceiveNetwork}</strong>...</p>
                      <Button onClick={generateUniqueAddress} disabled={generatingAddress} size="sm" className="gap-2">
                        {generatingAddress ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ArrowDownLeft className="w-4 h-4" />}
                        {generatingAddress ? 'جاري التوليد...' : 'توليد عنوان خاص بي'}
                      </Button>
                    </div>
                  );
                }

                const cryptoMeta = KNOWN_CRYPTO_META[selectedReceiveCrypto];
                return (
                  <div className="space-y-4">
                    <div className="rounded-2xl bg-white p-6 flex flex-col items-center gap-4 shadow-sm">
                      <QRCodeSVG value={displayAddress} size={200} level="H" />
                      <div className="flex items-center gap-1.5 bg-gray-100 px-3 py-1.5 rounded-full">
                        <span className="text-base">{cryptoMeta?.icon}</span>
                        <span className="text-xs font-medium text-gray-600">{selectedReceiveCrypto} • {selectedReceiveNetwork}</span>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground font-medium">{t('wallet.depositAddress') || 'عنوان الاستقبال'}</Label>
                      <div className="flex items-center gap-2 p-3 rounded-xl bg-secondary/60 cursor-pointer hover:bg-secondary transition-colors group" onClick={() => copyToClipboard(displayAddress)}>
                        <p className="flex-1 font-mono text-xs text-foreground break-all leading-relaxed" dir="ltr">{displayAddress}</p>
                        <Copy className="w-4 h-4 text-muted-foreground group-hover:text-primary shrink-0 transition-colors" />
                      </div>
                    </div>
                    <div className="p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20 flex gap-2">
                      <span className="text-yellow-500 shrink-0 mt-0.5">⚠️</span>
                      <p className="text-xs text-yellow-600 dark:text-yellow-400">
                        {t('wallet.networkWarning') || 'أرسل فقط'} {selectedReceiveCrypto} {t('wallet.networkWarning2') || 'عبر شبكة'} {selectedReceiveNetwork}. {t('wallet.networkWarningEnd') || 'إرسال عملات أخرى قد يؤدي لفقدانها نهائياً.'}
                      </p>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Swap Dialog */}
      <Dialog open={swapOpen} onOpenChange={(open) => { setSwapOpen(open); if (!open) { setSwapFrom(""); setSwapTo(""); setSwapAmount(""); } }}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden rounded-2xl">
          <div className="bg-gradient-to-b from-accent/5 to-transparent px-5 pt-5 pb-4">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center">
                  <ArrowLeftRight className="w-4 h-4 text-accent-foreground" />
                </div>
                {t('wallet.swapTitle')}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">{t('wallet.swapDesc')}</DialogDescription>
            </DialogHeader>
          </div>
          <div className="px-5 pb-5 space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-medium">{t('wallet.swapFromLabel')}</Label>
              <Select value={swapFrom} onValueChange={(v) => { setSwapFrom(v); if (v === swapTo) setSwapTo(""); }}>
                <SelectTrigger className="h-11 rounded-xl bg-secondary/60 border-0"><SelectValue placeholder={t('wallet.swapSelectCoin')} /></SelectTrigger>
                <SelectContent>
                  {balances.filter(b => b.balance > 0).map(b => {
                    const meta = KNOWN_CRYPTO_META[b.crypto_symbol];
                    return (
                      <SelectItem key={b.crypto_symbol} value={b.crypto_symbol}>
                        <div className="flex items-center gap-2">
                          <span>{meta?.icon}</span>
                          <span>{meta?.nameEn || b.crypto_name}</span>
                          <span className="text-muted-foreground text-xs">({b.balance})</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium">{t('wallet.quantityLabel')}</Label>
              <div className="relative">
                <Input type="number" value={swapAmount} onChange={e => setSwapAmount(e.target.value)} placeholder="0.00" dir="ltr" className="bg-secondary/60 border-0 h-11 rounded-xl font-mono" />
                {swapFromBalance && (
                  <button onClick={() => setSwapAmount(swapFromBalance.balance.toString())} className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-primary font-semibold bg-primary/10 px-2 py-0.5 rounded hover:bg-primary/15">{t('wallet.allBalance')}</button>
                )}
              </div>
              {swapFromBalance && <p className="text-[11px] text-muted-foreground">{t('wallet.availableBalance')}: {swapFromBalance.balance} {swapFrom}</p>}
            </div>

            <div className="flex justify-center">
              <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                <ArrowLeftRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium">{t('wallet.swapToLabel')}</Label>
              <Select value={swapTo} onValueChange={setSwapTo}>
                <SelectTrigger className="h-11 rounded-xl bg-secondary/60 border-0"><SelectValue placeholder={t('wallet.swapSelectCoin')} /></SelectTrigger>
                <SelectContent>
                  {Object.entries(KNOWN_CRYPTO_META).filter(([symbol]) => symbol !== swapFrom).map(([symbol, meta]) => (
                    <SelectItem key={symbol} value={symbol}>
                      <div className="flex items-center gap-2">
                        <span>{meta.icon}</span>
                        <span>{meta.nameEn}</span>
                        <span className="text-muted-foreground text-xs">({symbol})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {swapFrom && swapTo && swapAmountNum > 0 && (
              <div className="p-3 rounded-xl bg-secondary/40 border border-border/50 space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">{t('wallet.swapYouGet')}</span>
                  <span className="font-semibold text-foreground" dir="ltr">{swapReceiveAmount.toFixed(8).replace(/\.?0+$/, '')} {swapTo}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">{t('wallet.swapValueUsd')}</span>
                  <span className="text-foreground" dir="ltr">${swapValueUsd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">{t('wallet.swapFee')}</span>
                  <span className="text-foreground" dir="ltr">${swapFeeUsd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">{t('wallet.swapPrice')} {swapFrom}</span>
                  <span className="text-muted-foreground" dir="ltr">${swapFromPrice.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">{t('wallet.swapPrice')} {swapTo}</span>
                  <span className="text-muted-foreground" dir="ltr">${swapToPrice.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            )}

            <Button
              onClick={handleSwap}
              disabled={swapSubmitting || !swapFrom || !swapTo || swapAmountNum <= 0 || (swapFromBalance ? swapAmountNum > swapFromBalance.balance : true)}
              className="w-full h-12 rounded-xl font-semibold text-sm"
            >
              {swapSubmitting ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : null}
              {swapSubmitting ? t('wallet.swapping') : t('wallet.swapConfirm')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WalletPage;
